/* eslint-disable @typescript-eslint/no-explicit-any */
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import { autobind } from "core-decorators";
import { MapboxPolygonObject } from "@/components/mapbox/Lib/Objects/MapboxPolygonObject";
import type { VertexInfo } from "../Utils/ObjectUtils";
import type { UpdatePolygonFeatureDto } from "@/types/dto";

class EditorPolygonController extends BaseController {
  public static name = "EditorPolygonController";
  public static event = {
    COMPLETE: "COMPLETE",
  };

  private _selectedFeature: MapboxPolygonObject | null = null;
  private _selectedVertex: VertexInfo | null = null;
  private _isEditing: boolean = false;
  private _isDraggingPolygon: boolean = false; // New: for polygon dragging
  private _dragStartPosition: { lng: number; lat: number } | null = null; // New: drag start position
  private _originalCoordinates: number[][] | null = null; // New: backup of original coordinates

  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
    this._mapboxViewer = mapboxViewer;
    this._canvas.addEventListener("pointerdown", this.onPointerDownController);
    this._canvas.addEventListener("pointermove", this.onPointerMoveController);
  }

  get selectedFeature() {
    return this._selectedFeature;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
  }

  public get enabled() {
    return this._enabled;
  }

  @autobind
  attached(object: MapboxPolygonObject) {
    if (!this._enabled) {
      return;
    }
    if (object.content.config?.isLocked) {
      return;
    }
    this._selectedFeature = object;
    // Enable vertex editing mode
    this._selectedFeature.setEditing(true);
  }

  @autobind
  onPointerDownController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._selectedFeature) return;

    const canvasRect = this._canvas.getBoundingClientRect();
    this._onDownPosition.x = event.clientX - canvasRect.left;
    this._onDownPosition.y = event.clientY - canvasRect.top;

    // Store drag start position in geographic coordinates
    const dragStartLngLat = this._mapboxViewer._mapViewer.unproject([
      this._onDownPosition.x,
      this._onDownPosition.y,
    ]);
    this._dragStartPosition = {
      lng: dragStartLngLat.lng,
      lat: dragStartLngLat.lat,
    };

    // Query for vertex features first (highest priority)
    const vertexFeatures = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onDownPosition,
      {
        layers: ["polygon-vertices", "polygon-midpoints"],
        filter: [
          "all",
          ["==", ["get", "app"], "logistic-app"],
          ["==", ["get", "polygonId"], this._selectedFeature.contentId],
        ],
      }
    );

    if (vertexFeatures.length > 0) {
      // Vertex editing mode
      const vertexFeature = vertexFeatures[0];
      this._selectedVertex = {
        type: vertexFeature.properties?.vertexType,
        index: vertexFeature.properties?.vertexIndex,
        insertIndex: vertexFeature.properties?.insertIndex,
        featureId: vertexFeature.id,
      };

      this._mapboxViewer.toggleCamera(false);
      this._isEditing = true;
      this._isDraggingPolygon = false;
      this._canvas.addEventListener("pointerup", this.onPointerUpController);
      console.log("Selected vertex:", this._selectedVertex); // Debug log
      return;
    }

    // Query for polygon body - FIX: Use correct property name
    const polygonFeatures = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onDownPosition,
      {
        layers: ["polygon-fill", "polygon-outline"],
        filter: [
          "all",
          ["==", ["get", "app"], "logistic-app"],
          ["==", ["get", "contentId"], this._selectedFeature.contentId], // FIX: Use contentId instead of id
        ],
      }
    );

    if (polygonFeatures.length > 0) {
      // Polygon dragging mode
      this._isDraggingPolygon = true;
      this._isEditing = true;
      this._selectedVertex = null;

      // Store original coordinates for reference
      this._originalCoordinates = this._selectedFeature.content.coordinates.map(
        (coord) => [...coord]
      );

      this._mapboxViewer.toggleCamera(false);
      this._canvas.addEventListener("pointerup", this.onPointerUpController);

      // Show visual feedback
      this._selectedFeature.setDragHighlight(true);

      console.log("Started polygon drag mode"); // Debug log
    } else {
      console.log("No polygon features found at click position"); // Debug log
    }
  }

  @autobind
  onPointerMoveController(event: PointerEvent) {
    if (!this._enabled) return;
    if (!this._isEditing) return;
    if (!this._selectedFeature) return;
    if (!this._dragStartPosition) return;

    this._isDragging = true;

    const canvasRect = this._canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top,
    };

    const currentLngLat = this._mapboxViewer._mapViewer.unproject([
      point.x,
      point.y,
    ]);

    if (this._isDraggingPolygon && this._originalCoordinates) {
      // Calculate offset from drag start position
      const deltaLng = currentLngLat.lng - this._dragStartPosition.lng;
      const deltaLat = currentLngLat.lat - this._dragStartPosition.lat;

      // Move entire polygon by applying offset to all coordinates
      const newCoordinates = this._originalCoordinates.map((coord) => [
        coord[0] + deltaLng,
        coord[1] + deltaLat,
      ]);

      // Update the polygon coordinates
      this._selectedFeature.movePolygon(newCoordinates);
    } else if (this._selectedVertex) {
      // Vertex editing mode
      if (
        this._selectedVertex.type === "corner" &&
        this._selectedVertex.index !== undefined
      ) {
        // If offset editing is enabled, update offset vertex; otherwise update main polygon
        if (this._selectedFeature.isEditingOffset) {
          this._selectedFeature.updateOffsetVertex(this._selectedVertex.index, [
            currentLngLat.lng,
            currentLngLat.lat,
          ]);
        } else {
          this._selectedFeature.updateVertex(this._selectedVertex.index, [
            currentLngLat.lng,
            currentLngLat.lat,
          ]);
        }
      } else if (this._selectedVertex.type === "midpoint") {
        // For midpoints, don't do anything during move - we'll handle it on pointer up
        // Just show visual feedback that something is selected
      }
    }
  }

  @autobind
  onPointerUpController(event: PointerEvent) {
    if (!this._enabled || !this._selectedFeature) {
      this.cleanup();
      return;
    }

    this._mapboxViewer.toggleCamera(true);

    const canvasRect = this._canvas.getBoundingClientRect();
    const point = {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top,
    };

    if (this._isDraggingPolygon && this._isDragging) {
      // Finalize polygon move
      const finalLngLat = this._mapboxViewer._mapViewer.unproject([
        point.x,
        point.y,
      ]);

      if (this._dragStartPosition && this._originalCoordinates) {
        const deltaLng = finalLngLat.lng - this._dragStartPosition.lng;
        const deltaLat = finalLngLat.lat - this._dragStartPosition.lat;

        const finalCoordinates = this._originalCoordinates.map((coord) => [
          coord[0] + deltaLng,
          coord[1] + deltaLat,
        ]);

        // Update content with final coordinates
        const current = this._selectedFeature.content;
        let updatedProperties = { ...current.properties } as any;
        if (
          current.properties?.isManualOffset &&
          Array.isArray(current.properties?.offsetCoordinates) &&
          current.properties.offsetCoordinates.length > 0
        ) {
          updatedProperties = {
            ...updatedProperties,
          };
        }

        this._selectedFeature.updateContent({
          ...current,
          coordinates: finalCoordinates,
          properties: updatedProperties,
        });

        this.complete();
      }
    } else if (
      this._selectedVertex &&
      this._selectedVertex.type === "midpoint"
    ) {
      // Handle midpoint click - insert new vertex
      const lngLat = this._mapboxViewer._mapViewer.unproject([
        point.x,
        point.y,
      ]);

      if (this._selectedVertex.insertIndex !== undefined) {
        console.log(
          "Inserting vertex at index:",
          this._selectedVertex.insertIndex
        ); // Debug log
        this._selectedFeature.insertVertex(this._selectedVertex.insertIndex, [
          lngLat.lng,
          lngLat.lat,
        ]);
        this.complete();
      }
    } else if (
      this._selectedVertex &&
      this._selectedVertex.type === "corner" &&
      this._isDragging
    ) {
      // Handle corner vertex dragging completion
      this.complete();
    }

    this.cleanup();
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!this._selectedFeature || !this._selectedVertex) return;

    if (event.key === "Delete" || event.key === "Backspace") {
      if (
        this._selectedVertex.type === "corner" &&
        this._selectedVertex.index !== undefined
      ) {
        this._selectedFeature.removeVertex(this._selectedVertex.index);
        this._selectedVertex = null;
      }
    }
  }

  @autobind
  handleClick(event: PointerEvent) {
    this._mapboxViewer.toggleCamera(true);
    if (!this._enabled) return;
    if (!this._selectedFeature) return;
    if (event.button !== 0) return;

    // Only consider a click if no dragging occurred
    if (!this._onDownPosition.equals(this._onUpPosition)) {
      if (this._isEditing && this._isDragging) {
        this.complete();
      }
    }
    this.cleanup();
  }

  @autobind
  complete() {
    if (!this._selectedFeature) return;

    const updatedContent: UpdatePolygonFeatureDto = {
      ...this._selectedFeature.content,
      coordinates: this._selectedFeature.content.coordinates,
      properties: {
        ...this._selectedFeature.content.properties,
      },
    };

    // Dispatch completion event
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    this.dispatchEvent({
      type: EditorPolygonController.event.COMPLETE,
      content: updatedContent,
    });

    this.detached();
    this._mapboxViewer.controllers.interactionController?.editorDetach();
  }

  @autobind
  detached() {
    if (this._selectedFeature) {
      this._selectedFeature.setEditing(false);
      this._selectedFeature.setDragHighlight(false);
    }
    this.cleanup();
    this._selectedFeature = null;
  }

  @autobind
  private cleanup() {
    this._isEditing = false;
    this._isDragging = false;
    this._isDraggingPolygon = false;
    this._selectedVertex = null;
    this._dragStartPosition = null;
    this._originalCoordinates = null;
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);

    if (this._selectedFeature) {
      this._selectedFeature.setDragHighlight(false);
    }
  }

  /**
   * Helper method to determine where to insert a new vertex
   */
  @autobind
  protected getInsertionIndex(
    coordinates: number[][],
    newPoint: { lng: number; lat: number },
    closestIndex: number
  ): number {
    // Find the edge closest to the new point
    let minDistance = Infinity;
    let insertIndex = closestIndex + 1;

    for (let i = 0; i < coordinates.length - 1; i++) {
      const start = coordinates[i];
      const end = coordinates[i + 1];

      // Calculate distance from point to line segment
      const distance = this.pointToLineDistance(
        newPoint.lng,
        newPoint.lat,
        start[0],
        start[1],
        end[0],
        end[1]
      );

      if (distance < minDistance) {
        minDistance = distance;
        insertIndex = i + 1;
      }
    }

    return insertIndex;
  }

  /**
   * Calculate distance from point to line segment
   */
  private pointToLineDistance(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): number {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) return Math.sqrt(A * A + B * B);

    const param = dot / lenSq;
    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  @autobind
  dispose() {
    this._canvas.removeEventListener(
      "pointerdown",
      this.onPointerDownController
    );
    this._canvas.removeEventListener(
      "pointermove",
      this.onPointerMoveController
    );
    this._canvas.removeEventListener("pointerup", this.onPointerUpController);
    this.cleanup();
  }
}

export default EditorPolygonController;
